import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Breadcrumbs,
  Button,
  Heading,
  Link,
  Paragraph,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { CompletedTask, SafetyRepWork } from '../../api/safetyRepWork';
import { Routes } from '../../enums/Routes';
import {
  useSafetyRepWorkQuery,
  useUndoDocumentationAnswerMutation,
} from '../../hooks/queries/safetyRep/useSafetyRepWork';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { SafetyRepHero } from './components/SafetyRepHero';
import { WorkState } from './components/WorkState';
import {
  completedFacts,
  completedLink,
  completedTitle,
  OUTCOME_COLORS,
  timeline,
} from './safetyRepWorkView';
import layout from '../HRDashboard/hmsPage.module.css';
import classes from './SafetyRep.module.css';

/** What happened with one task the safety representative has answered */
export const SafetyRepCompleted = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const { taskKey } = useParams<{ taskKey: string }>();
  const { data, isLoading } = useSafetyRepWorkQuery(
    questionLanguageFor(i18n.language),
  );
  const task = data?.completed.find((candidate) => candidate.key === taskKey);

  return (
    <div className={layout.page} data-size="md">
      <SafetyRepHero title={t('safetyRep.signing.title')} />
      {!data ? (
        <WorkState loading={isLoading} error={t('safetyRep.work.error')} />
      ) : (
        <div className={classes.column}>
          {task ? (
            <Completed task={task} work={data} />
          ) : (
            <Alert data-color="warning">
              <Paragraph>{t('safetyRep.completed.notFound')}</Paragraph>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

/** A return of the documentation can be taken back until the work is archived. A signature is final. */
const Undo = ({ surveyId }: { surveyId: number }): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const undo = useUndoDocumentationAnswerMutation();
  return (
    <div className={classes.comment}>
      <Paragraph data-size="sm">{t('safetyRep.completed.undoText')}</Paragraph>
      <Button
        variant="secondary"
        data-size="sm"
        className={layout.startAligned}
        loading={undo.isPending}
        onClick={() =>
          undo.mutate(surveyId, {
            onSuccess: () => navigate(Routes.SafetyRepSigning),
          })
        }
      >
        {t('safetyRep.work.documentation.undo')}
      </Button>
      {undo.isError && (
        <ValidationMessage data-size="sm">
          {t('safetyRep.work.documentation.failed')}
        </ValidationMessage>
      )}
    </div>
  );
};

const Completed = ({
  task,
  work,
}: {
  task: CompletedTask;
  work: SafetyRepWork;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const title = completedTitle(task, t);
  const link = completedLink(task);
  const undoable =
    task.kind === 'documentation' &&
    work.documentation.some(
      (item) => item.surveyId === task.surveyId && item.status === 'RETURNED' && item.canUndo,
    );
  return (
    <>
      <Breadcrumbs aria-label={t('breadcrumbs.ariaLabel')}>
        <Breadcrumbs.List>
          <Breadcrumbs.Item>
            <Breadcrumbs.Link asChild>
              <RouterLink to={Routes.SafetyRepSigning}>
                {t('safetyRep.signing.title')}
              </RouterLink>
            </Breadcrumbs.Link>
          </Breadcrumbs.Item>
          <Breadcrumbs.Item>
            <Breadcrumbs.Link aria-current="page">{title}</Breadcrumbs.Link>
          </Breadcrumbs.Item>
        </Breadcrumbs.List>
      </Breadcrumbs>
      <div className={layout.cardHeading}>
        <span className={classes.taskMeta}>
          <Tag data-size="sm" data-color={OUTCOME_COLORS[task.outcome]}>
            {t(`safetyRep.completed.outcomes.${task.outcome}`)}
          </Tag>
          <Tag data-size="sm" data-color="neutral">
            {t('safetyRep.completed.done')}
          </Tag>
        </span>
        <Heading level={2} data-size="md">
          {title}
        </Heading>
        <Paragraph className={classes.text}>
          {t(`safetyRep.completed.what.${task.kind}`)}
        </Paragraph>
      </div>
      <section
        aria-labelledby="vo-summary"
        className={layout.card}
        data-gap="sm"
      >
        <Heading level={3} data-size="xs" id="vo-summary">
          {t('safetyRep.completed.summary')}
        </Heading>
        <dl className={classes.facts}>
          {completedFacts(task, t, i18n.language).map((fact) => (
            <div key={fact.key}>
              <dt className={`ds-paragraph ${layout.subtle}`} data-size="xs">
                {t(`safetyRep.completed.facts.${fact.key}`)}
              </dt>
              <dd className="ds-label" data-size="sm">
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>
        {task.comment && (
          <div className={classes.comment}>
            <span className="ds-label" data-size="sm">
              {t('safetyRep.completed.yourComment')}
            </span>
            <Paragraph data-size="sm">
              {t('safetyRep.completed.quoted', { comment: task.comment })}
            </Paragraph>
          </div>
        )}
        {link && (
          <Link asChild data-size="sm" className={layout.startAligned}>
            <RouterLink to={link}>
              {t(`safetyRep.completed.links.${task.kind}`)}
            </RouterLink>
          </Link>
        )}
        {undoable && task.surveyId !== null && (
          <Undo surveyId={task.surveyId} />
        )}
      </section>
      <section
        aria-labelledby="vo-history"
        className={layout.card}
        data-gap="sm"
      >
        <Heading level={3} data-size="xs" id="vo-history">
          {t('safetyRep.completed.history')}
        </Heading>
        <ol className={classes.timeline}>
          {timeline(task, t, i18n.language).map((event) => (
            <li key={event.key} data-last={event.last}>
              <span aria-hidden="true" className={classes.timelineDot} />
              <span className={classes.teamName}>
                <span
                  className="ds-paragraph"
                  data-size="sm"
                  data-last={event.last}
                >
                  {event.text}
                </span>
                <span
                  className={`ds-paragraph ${layout.subtle}`}
                  data-size="xs"
                >
                  {event.when}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </section>
    </>
  );
};
